import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Stack,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Close, Add, ContentCopy, Visibility, VisibilityOff, KeyboardArrowDown } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch } from "react-redux";
import { createShareLink } from "../../redux/actions/b2bActions";
// import TextBox from "./textbox";

interface SharePopupProps {
  open: boolean;
  onClose: () => void;
  selectedApplicantDetails?: any;
  selectedCandidates?: any[];
  jobId?: number;
  onShareSuccess?: (shareUrl: string) => void;
}

const SharePopup: React.FC<SharePopupProps> = ({ open, onClose, selectedApplicantDetails, selectedCandidates, jobId, onShareSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const dispatch = useDispatch();
  
  console.log('SharePopup render:', { open, onClose, isMobile });
  const [selectedOption, setSelectedOption] = useState("email");
  const [linkValue, setLinkValue] = useState("");

  // Email list state
  const [emails, setEmails] = useState<string[]>(["", ""]); // start with 2
  const [emailError, setEmailError] = useState<string>("");
  const [userSelectionError, setUserSelectionError] = useState<string>("");
  
  // Share link state
  const [shareUrl, setShareUrl] = useState<string>("");
  const [loadingShare, setLoadingShare] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [expiryDate, setExpiryDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedOption("email");
      setEmails(["", ""]); // reset when popup opens
      setEmailError(""); // reset error when popup opens
      setUserSelectionError(""); // reset user selection error when popup opens
      setShareUrl(""); // reset share URL when popup opens
      setPassword(""); // reset password when popup opens
      setShowPassword(false); // reset password visibility when popup opens
      setExpiryDate(null); // reset expiry date when popup opens
    }
  }, [open]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
    // Clear email error when switching options
    setEmailError("");
    setUserSelectionError("");
  };

  // Email handlers
  const handleEmailChange = (index: number, value: string) => {
    const updated = [...emails];
    updated[index] = value;
    setEmails(updated);
    // Clear error when user starts typing
    if (emailError) {
      setEmailError("");
    }
    if (userSelectionError) {
      setUserSelectionError("");
    }
  };

  // Email validation regex
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  // Validation function
  const validateEmails = () => {
    // Check if candidates are selected via checkbox
    if (!selectedCandidates || selectedCandidates.length === 0) {
      setUserSelectionError("Please select at least one candidate to share");
      return false;
    }

    if (selectedOption === "email") {
      const nonEmptyEmails = emails.filter(email => email.trim() !== "");
      
      if (nonEmptyEmails.length === 0) {
        setEmailError("Please enter at least one email address");
        return false;
      }

      // Check if all non-empty emails are valid
      const invalidEmails = nonEmptyEmails.filter(email => !emailRegex.test(email.trim()));
      
      if (invalidEmails.length > 0) {
        setEmailError("Please enter valid email addresses");
        return false;
      }
    }
    return true;
  };

  const handleAddEmail = () => {
    setEmails([...emails, ""]);
  };

  const handleRemoveEmail = (index: number) => {
    const updated = [...emails];
    updated.splice(index, 1);
    setEmails(updated);
  };

  // Handle share button click
  const handleShareClick = () => {
    if (validateEmails()) {
      if (!jobId || !selectedCandidates || selectedCandidates.length === 0) {
        setUserSelectionError("Please select at least one candidate to share");
        return;
      }

      setLoadingShare(true);
      
      const nonEmptyEmails = emails.filter(email => email.trim() !== "");
      const applicantIds = selectedCandidates.map(candidate => candidate.applicationId);
      // Pass the date value in ISO format, or null if no date selected
      const expiryDays = expiryDate ? expiryDate.format('YYYY-MM-DD') : null;

      dispatch(createShareLink(
        jobId,
        applicantIds,
        nonEmptyEmails,
        expiryDays,
        password,
        (response: any) => {
          setLoadingShare(false);
          if (response?.result?.uniqueToken) {
            setShareUrl(response.result.uniqueToken);
            if (selectedOption === "link") {
              setLinkValue(response.result.uniqueToken);
            }
            // Construct full share URL using frontend base URL
            const frontendBaseUrl =   window.location.origin;
            const fullShareUrl = `${frontendBaseUrl}/shareprofile/${jobId}/${response.result.uniqueToken}`;
            
            // Close the share popup and trigger success popup in parent
            onClose();
            if (onShareSuccess) {
              onShareSuccess(fullShareUrl);
            }
            
            // Clear the form after showing success
            setEmails(["", ""]);
            setPassword("");
            setExpiryDate(null);
            setEmailError("");
            setUserSelectionError("");
          }
        },
        () => {
          setLoadingShare(false);
        }
      ));
    }
  };

  // Handle copy link button click
  const handleCopyLinkClick = () => {
    if (!jobId || !selectedCandidates || selectedCandidates.length === 0) {
      setUserSelectionError("Please select at least one candidate to share");
      return;
    }

    setLoadingShare(true);
    
    const nonEmptyEmails = emails.filter(email => email.trim() !== "");
    const applicantIds = selectedCandidates.map(candidate => candidate.applicationId);
    // Pass the date value in ISO format, or null if no date selected
    const expiryDays = expiryDate ? expiryDate.format('YYYY-MM-DD') : null;

    dispatch(createShareLink(
      jobId,
      applicantIds,
      nonEmptyEmails,
      expiryDays,
      password,
      (response: any) => {
        setLoadingShare(false);
        if (response?.result?.uniqueToken) {
          setShareUrl(response.result.uniqueToken);
          setLinkValue(response.result.uniqueToken);
          // Construct full share URL using frontend base URL
          const frontendBaseUrl = window.location.origin;
          const fullShareUrl = `${frontendBaseUrl}/shareprofile/${jobId}/${response.result.uniqueToken}`;
          
          // Close the share popup and trigger success popup in parent
          onClose();
          if (onShareSuccess) {
            onShareSuccess(fullShareUrl);
          }
          
          // Clear the form after showing success
          setEmails(["", ""]);
          setPassword("");
          setExpiryDate(null);
          setEmailError("");
          setUserSelectionError("");
        }
      },
      () => {
        setLoadingShare(false);
      }
    ));
  };

  console.log('SharePopup Dialog render - open:', open, 'isMobile:', isMobile, 'selectedOption:', selectedOption);
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? "0px" : "18px",
          display: "flex",
          flexDirection: "column",
          width: isMobile ? "100%" : "500px",
          height: isMobile ? "100%" : "auto",
          maxHeight: isMobile ? "100%" : "90vh",
          margin: isMobile ? "0" : "32px",
          p: 0,
          zIndex: 9999,
          position: isMobile ? "fixed" : "relative",
          top: isMobile ? "0" : "auto",
          left: isMobile ? "0" : "auto",
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: isMobile ? "none" : "blur(15px)",
          backgroundColor: isMobile ? "rgba(0, 0, 0, 0.5)" : "rgba(103, 103, 103, 0.59)",
          zIndex: 9998,
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: isMobile ? "16px 20px" : "24px",
          }}
        >
          <Box>
            <Typography
              fontFamily={"DM Sans"}
              fontWeight={500}
              fontSize={isMobile ? "20px" : "28px"}
              fontStyle={"Medium"}
            >
              Share Candidate
            </Typography>
          </Box>

          <Box>
            <IconButton onClick={onClose} sx={{ padding: isMobile ? "4px" : "8px" }}>
              <Close sx={{ color: "black", fontSize: isMobile ? "20px" : "24px" }} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: isMobile ? "16px 20px 16px 20px" : "24px 48px 24px 24px", overflow: "auto" }}>
        {/* Radio Buttons */}
        <RadioGroup
          row={!isMobile}
          value={selectedOption}
          onChange={handleChange}
          sx={{ marginBottom: isMobile ? "16px" : "24px" }}
        >
          <FormControlLabel
            value="email"
            control={
              <Radio
                sx={{
                  color: "#888",
                  "&.Mui-checked": {
                    color: "#756DE9",
                  },
                }}
              />
            }
            label={<Typography sx={{ fontSize: isMobile ? "14px" : "16px" }}>Send Email</Typography>}
          />
          <FormControlLabel
            value="link"
            control={
              <Radio
                sx={{
                  color: "#888",
                  fontSize: "16px",
                  fontWeight: "400",
                  "&.Mui-checked": {
                    color: "#756DE9",
                  },
                }}
              />
            }
            label={<Typography sx={{ fontSize: isMobile ? "14px" : "16px" }}>Get Link</Typography>}
          />
        </RadioGroup>

        {selectedOption === "email" ? (
          <Stack spacing={isMobile ? 2 : 1}>
            {/* Email */}
            <Box sx={{fontFamily: "DM Sans"}}>
              <span style={{ fontWeight:"400", fontSize: isMobile ? "14px" : "16px"}}>Email</span>
            </Box>

            {emails.map((email, index) => {
              const isEmailInvalid = email.trim() !== "" && !emailRegex.test(email.trim());
              
              return (
                <TextField
                  key={index}
                  placeholder="Enter email"
                  type="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEmailChange(index, e.target.value)}
                  fullWidth
                  variant="outlined"
                  error={isEmailInvalid}
                  helperText={isEmailInvalid ? "Please enter a valid email address" : ""}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {emails.length > 1 && (
                          <IconButton onClick={() => handleRemoveEmail(index)}>
                            <Close />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    marginBottom: 2,
                    borderRadius: "999px",
                    backgroundColor: isEmailInvalid ? "rgba(244, 67, 54, 0.02)" : "rgba(75, 68, 212, 0.02)",
                    boxShadow: isEmailInvalid ? "inset 0px 0px 11px 2px rgba(244, 67, 54, 0.08)" : "inset 0px 0px 11px 2px rgba(75, 68, 212, 0.08)",
                    fontFamily: "DM Sans",
                    fontSize: "14px",
                    fontWeight: 400,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: "999px",
                      '& fieldset': {
                        border: "none",
                      },
                    },
                    px: 2,
                    py: 0.5,
                  }}
                />
              );
            })}

            {/* Add button */}
            <Box>
              <Button onClick={handleAddEmail} sx={{color:"rgba(117, 109, 233, 1)",mb:1,textTransform:"none"}}>
                + Add
              </Button>
            </Box>

            {/* Password */}
            <Box>
      {/* Label of password */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          fontFamily: "DM Sans",
        }}
      >
        <span style={{ fontWeight:"400", fontSize: "16px"}}>Set password</span>
        <span style={{ color: "rgba(183, 179, 235, 1)", fontSize: "16px", fontWeight: 400 }}>
          Optional
        </span>
      </Box>
      </Box>
            <TextField
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ padding: "4px" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ 
                marginBottom: 2,
                borderRadius: "999px",
                backgroundColor: "rgba(75, 68, 212, 0.02)",
                boxShadow: "inset 0px 0px 11px 2px rgba(75, 68, 212, 0.08)",
                fontFamily: "DM Sans",
                fontSize: "14px",
                fontWeight: 400,
                '& .MuiOutlinedInput-root': {
                  borderRadius: "999px",
                  '& fieldset': {
                    border: "none",
                  },
                },
                px: 2,
                py: 0.5,
              }}
            />

            {/* Error Messages */}
            {emailError && (
              <Typography
                sx={{
                  color: "#f44336",
                  fontSize: "14px",
                  marginBottom: 1,
                  fontFamily: "DM Sans",
                }}
              >
                {emailError}
              </Typography>
            )}
            {userSelectionError && (
              <Typography
                sx={{
                  color: "#f44336",
                  fontSize: "14px",
                  marginBottom: 1,
                  fontFamily: "DM Sans",
                }}
              >
                {userSelectionError}
              </Typography>
            )}
            {shareUrl && (
              <Typography
                sx={{
                  color: "#4CAF50",
                  fontSize: "14px",
                  marginBottom: 1,
                  fontFamily: "DM Sans",
                }}
              >
                Share link created successfully! You can now copy the link.
              </Typography>
            )}

            {/* Share Button */}
            <Button
              variant="contained"
              onClick={handleShareClick}
              disabled={loadingShare}
              sx={{
                backgroundColor: "#756DE9",
                width: "199px",
                height: "55px",
                borderRadius: "95px",
                textTransform: "none",
                ":hover": { backgroundColor: "#7c75e8ff" },
                opacity: loadingShare ? 0.7 : 1,
              }}
            >
              {loadingShare ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CircularProgress size={20} sx={{ color: '#fff' }} />
                  <Typography sx={{ color: '#fff', fontSize: '14px' }}>
                    Creating...
                  </Typography>
                </Box>
              ) : (
                "Share Candidate"
              )}
            </Button>
          </Stack>
        ) : (
          <Stack spacing={isMobile ? 2 : 1}>
            <Box>
      {/* Label of set password*/}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          fontFamily: "DM Sans",
        }}
      >
        <span style={{ fontWeight:"400", fontSize: "16px"}}>Set password</span>
        <span style={{ color: "rgba(183, 179, 235, 1)", fontSize: "16px", fontWeight: 400 }}>
          Optional
        </span>
      </Box>
      </Box>
            <TextField
              placeholder="Enter password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ padding: "4px" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ 
                marginBottom: 2,
                borderRadius: "999px",
                backgroundColor: "rgba(75, 68, 212, 0.02)",
                boxShadow: "inset 0px 0px 11px 2px rgba(75, 68, 212, 0.08)",
                fontFamily: "DM Sans",
                fontSize: "14px",
                fontWeight: 400,
                '& .MuiOutlinedInput-root': {
                  borderRadius: "999px",
                  '& fieldset': {
                    border: "none",
                  },
                },
                px: 2,
                py: 0.5,
              }}
            />
             <Box>
      {/* Label of Expiring in*/}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          fontFamily: "DM Sans",
        }}
      >
        <span style={{ fontWeight:400, fontSize: "16px"}}>Expiring in</span>
        <span style={{ color: "rgba(183, 179, 235, 1)", fontSize: "16px", fontWeight: 400 }}>
          Optional
        </span>
      </Box>
      </Box>

         <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={expiryDate}
            onChange={(newValue) => setExpiryDate(newValue)}
            minDate={dayjs()}
            format="DD MMM YYYY"
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "outlined",
                placeholder: "Select expiry date",
                sx: { 
                  marginBottom: 2,
                  borderRadius: "999px",
                  backgroundColor: "rgba(75, 68, 212, 0.02)",
                  boxShadow: "inset 0px 0px 11px 2px rgba(75, 68, 212, 0.08)",
                  fontFamily: "DM Sans",
                  fontSize: "14px",
                  fontWeight: 400,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "999px",
                    '& fieldset': {
                      border: "none",
                    },
                  },
                  px: 2,
                  py: 0.5,
                },
              },
            }}
          />
        </LocalizationProvider>
            <Button
              variant="contained"
              onClick={handleCopyLinkClick}
              disabled={loadingShare}
              sx={{
                backgroundColor: "#756DE9",
                width: "157px",
                height: "55px",
                borderRadius: "95px",
                textTransform: "none",
                ":hover": { backgroundColor: "#7c75e8ff" },
                opacity: loadingShare ? 0.5 : 1,
              }}
            >
              {loadingShare ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                  <Typography sx={{ color: '#fff', fontSize: '12px' }}>
                    Creating...
                  </Typography>
                </Box>
              ) : (
                "Copy Link"
              )}
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SharePopup;
